import { Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useId } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { kr } from '../../lib/money';
import { trpc } from '../../lib/trpc';
import { TableSkeleton } from '../common/Skeleton';
import { BillHistoryPeriodList } from './BillHistory';
import { CostHistoryChart, CostSparkline } from './CostHistoryChart';
import {
  type CostHistoryBill,
  type CostHistoryPeriod,
  periodKey,
  periodLabel,
  periodRangeLabel,
  trendVsAverage,
} from './cost-history';

const TREND_TONE = {
  up: 'bg-warning-soft text-warning',
  down: 'bg-positive-soft text-positive',
  flat: 'bg-muted text-muted-foreground',
} as const;

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg bg-muted/50 px-3 py-2">
      <div className="truncate text-xs text-muted-foreground">{label}</div>
      <div className="truncate text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function BillDetails({
  bill,
  periods,
  months,
}: {
  bill: CostHistoryBill;
  periods: CostHistoryPeriod[];
  months: number;
}) {
  const historyQuery = trpc.recurringBill.history.useQuery(
    { billId: bill.id, months },
    { retry: false },
  );
  const ranges = new Map(periods.map((p) => [periodKey(p), periodRangeLabel(p)]));
  const { stats } = bill;

  return (
    <div className="space-y-4 border-t border-border px-4 pt-4 pb-4 sm:px-5">
      <CostHistoryChart
        points={bill.points}
        average={stats.average}
        height={{ base: 180, sm: 220 }}
        ariaLabel={`${bill.name} per period`}
      />

      <div className="grid grid-cols-3 gap-2">
        <Stat label="Snitt" value={stats.average != null ? kr(stats.average) : '–'} />
        <Stat label="Lägst" value={stats.min != null ? kr(stats.min) : '–'} />
        <Stat label="Högst" value={stats.max != null ? kr(stats.max) : '–'} />
      </div>
      {bill.expectedAmount != null && (
        <p className="text-xs text-muted-foreground">
          Förväntat belopp: <span className="tabular-nums">{kr(bill.expectedAmount)}</span>
        </p>
      )}

      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-sm font-semibold">Perioder</h3>
          <Link
            to="/bills"
            search={{ bill: bill.id }}
            className="inline-flex min-h-11 items-center text-sm font-medium text-primary sm:min-h-0"
          >
            Öppna räkningen
          </Link>
        </div>
        {historyQuery.isLoading ? (
          <TableSkeleton rows={4} columns={2} />
        ) : historyQuery.error ? (
          <p className="text-sm text-negative">Kunde inte hämta perioderna just nu.</p>
        ) : (
          <BillHistoryPeriodList
            rows={historyQuery.data ?? []}
            describePeriod={(year, month) => ranges.get(periodKey({ year, month }))}
          />
        )}
      </div>
    </div>
  );
}

/** One bill: latest cost, trend vs its average and a sparkline; tap for details. */
export function CostHistoryBillCard({
  bill,
  periods,
  months,
  expanded,
  onToggle,
}: {
  bill: CostHistoryBill;
  periods: CostHistoryPeriod[];
  months: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const detailsId = useId();
  const { stats } = bill;
  const trend = trendVsAverage(stats.changeVsAverage);
  const latestPeriod = stats.latestPeriod;

  return (
    <li className="overflow-hidden rounded-2xl border border-border bg-card">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={detailsId}
        className="block w-full px-4 pt-3.5 pb-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 sm:px-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-[15px] font-semibold">{bill.name}</span>
              {!bill.isActive && (
                <Badge variant="outline" className="text-muted-foreground">
                  Inaktiv
                </Badge>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              {trend && (
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 font-medium tabular-nums',
                    TREND_TONE[trend.tone],
                  )}
                >
                  {trend.label}
                </span>
              )}
              <span>
                {latestPeriod
                  ? `Senast ${periodLabel(latestPeriod.year, latestPeriod.month)}`
                  : 'Ingen betalning i perioden'}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <span className="text-base font-semibold tabular-nums">
              {stats.latest != null ? kr(stats.latest) : '–'}
            </span>
            <ChevronDown
              aria-hidden
              className={cn(
                'size-4 text-muted-foreground transition-transform',
                expanded && 'rotate-180',
              )}
            />
          </div>
        </div>
        {/* The expanded chart below replaces the sparkline */}
        {!expanded && (
          <div className="mt-2.5">
            <CostSparkline points={bill.points} />
          </div>
        )}
      </button>

      <div id={detailsId} hidden={!expanded}>
        {expanded && <BillDetails bill={bill} periods={periods} months={months} />}
      </div>
    </li>
  );
}
